import { getDatabase, ref, get, set, update, remove, push } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Tenant, TenantInput } from '../types/tenants';
import { TenantKind } from '../types/Enums';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { requireAdmin } from './adminGuard';
import { leaseInvolvesTenant, leaseStatus, todayStockholm } from '../Helpers/leaseUtils';
import { CoTenantsMap } from '../types/leases';
import { KeyHolder } from '../types/keyRegister';
import { keyReferencesHolder, keysHeldBy } from '../Helpers/keyRegisterUtils';
import { asRecord, parseKeyHolder, parseRegisterKeys } from './keyRegisterParser';
import { recordActivity } from './activityApi';

const TENANT_KINDS = Object.values(TenantKind) as string[];

const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) result[key] = entry;
    }
    return result as T;
};

const nullForUndefined = (value: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        result[key] = entry === undefined ? null : entry;
    }
    return result;
};

const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

/**
 * Tenants at `tenants/{tenantKey}`. Reads need a listed user; every mutation
 * needs an admin (parking-tenants plan D6). A tenant with leases or queue
 * entries is anonymised instead of deleted (parking-queue spec FR28) — lease
 * and queue history must survive.
 */

export type TenantDeleteResult = 'deleted' | 'anonymized';

interface AnonymizeTenantRequest {
    tenantKey: string;
}

interface AnonymizeTenantResponse {
    success: boolean;
    entriesAnonymized: number;
}

type RawLease = { tenantKey?: string; coTenants?: CoTenantsMap; startDate?: string; endDate?: string } | null;
type RawQueueEntry = { tenantKey?: string; status?: string } | null;

const namesHolder = (value: unknown, holder: KeyHolder): boolean => {
    const parsed = parseKeyHolder(value);
    return !!parsed && parsed.kind === holder.kind && parsed.key === holder.key;
};

/**
 * Whether a raw `keyRegister/keys/{k}` node names the holder anywhere: its `holder` or any
 * `log/{eventKey}/holder`. The safety net for what the parser drops (a malformed node or event,
 * a stale holder on a key no longer handed out): such history still names the tenant.
 */
const rawKeyNamesHolder = (node: unknown, holder: KeyHolder): boolean => {
    const n = asRecord(node);
    return namesHolder(n.holder, holder) || Object.values(asRecord(n.log)).some(event => namesHolder(asRecord(event).holder, holder));
};

const errorMessage = (err: unknown, fallback: string): string =>
    err instanceof Error && err.message ? err.message : fallback;
export class TenantsApi {
    private db = getDatabase();
    private functions = getFunctions(undefined, 'europe-north1');

    getTenantPath(tenantKey: string): string {
        return `tenants/${tenantKey}`;
    }

    private stamp(uid: string): { updatedBy: string; updatedWhen: string } {
        return { updatedBy: uid, updatedWhen: formatDateForStorage(new Date()) };
    }

    // MARK: - Parsing

    parseTenant(data: unknown, key: string): Tenant | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        if (typeof d.name !== 'string' || d.name.trim().length === 0) return null;
        const kind = typeof d.kind === 'string' && TENANT_KINDS.includes(d.kind)
            ? (d.kind as TenantKind)
            : TenantKind.Person;
        return stripUndefined({
            key,
            kind,
            name: d.name.trim(),
            orgNumber: optionalString(d.orgNumber),
            email: optionalString(d.email),
            phone: optionalString(d.phone),
            // Legacy single-line `address` (pre 2026-09-26) is shown as the street until the next save rewrites it.
            street: optionalString(d.street) ?? optionalString(d.address),
            postalCode: optionalString(d.postalCode),
            city: optionalString(d.city),
            comment: optionalString(d.comment),
            anonymized: d.anonymized === true ? (true as const) : undefined,
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
            updatedBy: optionalString(d.updatedBy),
            updatedWhen: optionalString(d.updatedWhen),
        });
    }

    parseTenants(data: unknown): Tenant[] {
        if (!data || typeof data !== 'object') return [];
        const tenants: Tenant[] = [];
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            const tenant = this.parseTenant(value, key);
            if (tenant) tenants.push(tenant);
        }
        return tenants.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }

    // MARK: - Reads

    async getAll(): Promise<Tenant[]> {
        const snapshot = await get(ref(this.db, 'tenants'));
        return snapshot.exists() ? this.parseTenants(snapshot.val()) : [];
    }

    async get(tenantKey: string): Promise<Tenant | null> {
        const snapshot = await get(ref(this.db, this.getTenantPath(tenantKey)));
        return snapshot.exists() ? this.parseTenant(snapshot.val(), tenantKey) : null;
    }

    // MARK: - Mutations

    private validateInput(input: TenantInput): TenantInput {
        const name = (input.name || '').trim();
        if (!name) {
            throw new Error('Namn krävs');
        }
        if (!TENANT_KINDS.includes(input.kind)) {
            throw new Error('Ogiltig typ');
        }
        return {
            kind: input.kind,
            name,
            orgNumber: input.kind === TenantKind.Company ? optionalString(input.orgNumber) : undefined,
            email: optionalString(input.email),
            phone: optionalString(input.phone),
            street: optionalString(input.street),
            postalCode: optionalString(input.postalCode),
            city: optionalString(input.city),
            comment: optionalString(input.comment),
        };
    }

    async create(input: TenantInput): Promise<Tenant> {
        const uid = await requireAdmin(this.db, 'skapa hyresgäster');
        const validated = this.validateInput(input);

        const newRef = push(ref(this.db, 'tenants'));
        const key = newRef.key;
        if (!key) {
            throw new Error('Kunde inte skapa nyckel');
        }

        const tenant: Tenant = stripUndefined({
            key,
            ...validated,
            createdBy: uid,
            createdWhen: formatDateForStorage(new Date()),
        });
        await set(ref(this.db, this.getTenantPath(key)), tenant);
        recordActivity('tenant.created', { targetKey: key });
        return tenant;
    }

    async update(tenantKey: string, input: TenantInput): Promise<void> {
        const uid = await requireAdmin(this.db, 'ändra hyresgäster');
        const validated = this.validateInput(input);
        await update(ref(this.db, this.getTenantPath(tenantKey)), {
            ...nullForUndefined({ ...validated }),
            address: null, // legacy single-line field, replaced by street/postalCode/city
            ...this.stamp(uid),
        });
        recordActivity('tenant.updated', { targetKey: tenantKey });
    }

    /**
     * Refused while the tenant has an active or upcoming lease (as primary or
     * co-tenant), an active queue entry or a key handed out to them (key
     * register, `keysHeldBy`; any key event naming them counts as a
     * reference). Hard-deletes the node when nothing references the tenant at
     * all; otherwise the `anonymizeTenant` callable strips the personal data
     * server-side and the node, leases and entries stay. Co-tenants are map
     * keys, which RTDB cannot index, so the whole (small) leases root is read.
     * Leases and queue entries are scanned raw on purpose, and key nodes raw
     * as well as parsed: a node or event the parser would drop as malformed
     * still references the tenant, and anonymising is the safe side.
     */
    async delete(tenantKey: string): Promise<TenantDeleteResult> {
        await requireAdmin(this.db, 'ta bort hyresgäster');
        const [leasesSnapshot, queueSnapshot, keysSnapshot] = await Promise.all([
            get(ref(this.db, 'leases')),
            get(ref(this.db, 'parkingQueue')),
            get(ref(this.db, 'keyRegister/keys')),
        ]);
        const leases = Object.values((leasesSnapshot.exists() ? leasesSnapshot.val() : {}) as Record<string, RawLease>)
            .filter((lease): lease is NonNullable<RawLease> => !!lease && typeof lease === 'object')
            .filter(lease => leaseInvolvesTenant({ tenantKey: lease.tenantKey ?? '', coTenants: lease.coTenants }, tenantKey));
        const entries = Object.values((queueSnapshot.exists() ? queueSnapshot.val() : {}) as Record<string, RawQueueEntry>)
            .filter((entry): entry is NonNullable<RawQueueEntry> => !!entry && typeof entry === 'object')
            .filter(entry => entry.tenantKey === tenantKey);

        const today = todayStockholm();
        const hasOpenLease = leases.some(lease =>
            typeof lease.startDate === 'string' && leaseStatus({ startDate: lease.startDate, endDate: lease.endDate }, today) !== 'ended'
        );
        if (hasOpenLease) {
            throw new Error('Personen har ett pågående eller kommande hyresavtal och kan inte tas bort');
        }
        if (entries.some(entry => entry.status === 'active')) {
            throw new Error('Personen står aktivt i parkeringskön och kan inte tas bort');
        }

        const holder: KeyHolder = { kind: 'tenant', key: tenantKey };
        const rawKeys = keysSnapshot.exists() ? keysSnapshot.val() : null;
        const keys = parseRegisterKeys(rawKeys);
        if (keysHeldBy(keys, holder).length > 0) {
            throw new Error('Personen har nycklar ute och kan inte tas bort');
        }
        // Parsed first; the raw scan adds only what the parser dropped (see rawKeyNamesHolder).
        const keyReferences = keys.some(key => keyReferencesHolder(key, holder))
            || Object.values(asRecord(rawKeys)).some(node => rawKeyNamesHolder(node, holder));

        if (leases.length === 0 && entries.length === 0 && !keyReferences) {
            await remove(ref(this.db, this.getTenantPath(tenantKey)));
            recordActivity('tenant.deleted', { targetKey: tenantKey });
            return 'deleted';
        }

        const anonymizeTenant = httpsCallable<AnonymizeTenantRequest, AnonymizeTenantResponse>(this.functions, 'anonymizeTenant');
        try {
            const result = await anonymizeTenant({ tenantKey });
            if (!result.data?.success) {
                throw new Error('Kunde inte anonymisera uppgifterna');
            }
        } catch (err) {
            throw new Error(errorMessage(err, 'Kunde inte anonymisera uppgifterna'));
        }
        recordActivity('tenant.anonymized', { targetKey: tenantKey });
        return 'anonymized';
    }
}

export const tenantsApi = new TenantsApi();
